<template>
  <div class="sv-timeline" data-test="sv-timeline">
    <p v-if="!timeline.appointmentCount" class="sv-timeline__empty" data-test="sv-timeline-empty">
      No appointments to show for this day.
    </p>

    <section
      v-for="group in timeline.hours"
      :key="`h-${group.hour}`"
      class="sv-tl-hour"
      :class="group.state ? `sv-tl-hour--${group.state}` : ''"
      :data-test="`sv-tl-hour-${group.hour}`"
    >
      <div class="sv-tl-hour__rail">
        <span class="sv-tl-hour__label">{{ group.label }}</span>
      </div>

      <div class="sv-tl-hour__body">
        <template v-for="item in group.items">
          <!--
            NOW. After everything that has already started, so an appointment in progress sits
            above the line and the next patient below it — the one question a phone at the front
            desk is opened to answer.
          -->
          <div
            v-if="item.kind === 'now'"
            :key="item.key"
            class="sv-tl-now"
            data-test="sv-tl-now"
          >
            <span class="sv-tl-now__pill">Now · {{ formatTime(item.start) }}</span>
          </div>

          <div
            v-else-if="item.kind === 'blockout'"
            :key="item.key"
            class="sv-tl-blockout"
            :data-test="`sv-tl-blockout-${item.key}`"
          >
            <span class="sv-tl-blockout__name">{{ item.name || 'Blockout' }}</span>
            <span class="sv-tl-blockout__meta">
              {{ formatRange(item.start, item.end) }} · {{ chairsLabel(item) }}
            </span>
          </div>

          <AppointmentCard
            v-else
            :key="item.key"
            :appt="item.appt"
            :view-settings="viewSettings"
            :bucket-map="bucketMap"
            :alert-payload="alertPayload"
            :accent="stripeFor(item.appt)"
            :op-name="opName(item.appt.Op)"
            :provider-label="provider(item.appt).label"
            :provider-title="provider(item.appt).title"
            :checklist="checklists[item.appt.AptNum] || null"
            :checklist-mode="checklistMode"
            @select="$emit('select-appointment', $event)"
            @card-enter="$emit('card-enter', $event)"
            @card-leave="$emit('card-leave', $event)"
            @alert-focus="$emit('alert-focus', $event)"
            @open-checklist="$emit('open-checklist', $event)"
          />
        </template>
      </div>
    </section>

    <p v-if="timeline.unplaced.length" class="sv-timeline__unplaced" data-test="sv-unplaced">
      {{ timeline.unplaced.length }}
      {{ timeline.unplaced.length === 1 ? 'appointment is' : 'appointments are' }}
      not in a visible operatory (hidden in Open Dental, or no chair assigned) and
      {{ timeline.unplaced.length === 1 ? 'is' : 'are' }} not shown above.
    </p>
  </div>
</template>

<script>
/**
 * The phone schedule (Phase 6): the day as ONE column of cards, every chair interleaved by start
 * time under an hour rail, each card naming its chair.
 *
 * Nilay's decision (2026-09-19): a phone's default is the vertical timeline — it is still what a phone
 * opens on. On a 390px screen the operatory grid shows two chairs at a time and makes you remember
 * which one you are looking at, so it was not the default; since 2026-10-23 (WP-B) it is the phone's
 * OPT-IN "Columns" view, beside this Timeline and the Table, chosen with the switch under the filters.
 *
 * Takes the same props as ScheduleGrid, so ScheduleView swaps one for the other without either
 * knowing. All the maths — grouping, order, blockout folding, where NOW goes, what cannot be
 * placed — is `buildTimeline()` in utils/scheduleView/layout.js, pure and node-tested; this file
 * only turns it into markup.
 */
import AppointmentCard from './AppointmentCard.vue';
import {
  buildTimeline, formatMinutes, formatTimeRange, appointmentProviderNum,
} from '@/utils/scheduleView/layout';
import { indexProviders, providerStripeColor } from '@/utils/scheduleView/provColor';

export default {
  name: 'ScheduleTimeline',

  components: { AppointmentCard },

  props: {
    /** The /schedule-view/day payload, already narrowed by the filters. */
    day: { type: Object, required: true },
    /** The UNFILTERED payload — here only so a provider keeps its colour when the view is narrowed. */
    unfilteredDay: { type: Object, default: null },
    viewSettings: { type: Object, required: true },
    bucketMap: { type: Object, default: () => ({}) },
    alertPayload: { type: Object, default: null },
    /** Minutes past midnight in the SITE's timezone. */
    nowMinutes: { type: Number, default: null },
    isToday: { type: Boolean, default: false },
    /** AptNum → { categories, totals }, built once at the page; see ScheduleGrid. */
    checklists: { type: Object, default: () => ({}) },
    /** 'hover' | 'pop' | 'sheet', decided by the page's ChecklistLayer. */
    checklistMode: { type: String, default: 'hover' },
  },

  computed: {
    operatories() {
      return this.day.operatories || [];
    },

    timeline() {
      return buildTimeline({
        appointments: this.day.appointments,
        operatories: this.operatories,
        blockouts: this.viewSettings.showBlockouts ? this.day.blockouts : [],
        isToday: this.isToday,
        nowMinutes: this.nowMinutes,
      });
    },

    opIndex() {
      const idx = {};
      this.operatories.forEach((op) => { idx[op.OperatoryNum] = op; });
      return idx;
    },

    /** ProvNum -> provider, from the UNFILTERED day when there is one (see ScheduleGrid). */
    providerIndex() {
      return indexProviders((this.unfilteredDay || this.day).providers);
    },
  },

  methods: {
    /** The card's left stripe: the provider's Open Dental colour, or neutral grey — never the chair's. */
    stripeFor(appt) {
      return providerStripeColor(appt, this.providerIndex, { appointmentProviderNum });
    },

    opName(operatoryNum) {
      const op = this.opIndex[operatoryNum];
      return op ? (op.OpName || op.Abbrev || `Operatory ${operatoryNum}`) : '';
    },

    provider(appt) {
      const p = this.providerIndex[appointmentProviderNum(appt)];
      if (!p) return { label: '', title: '' };
      const full = `${p.FName || ''} ${p.LName || ''}`.trim();
      return {
        label: p.Abbr || `${(p.FName || '?')[0]}${(p.LName || '')[0] || ''}`,
        title: full,
      };
    },

    chairsLabel(item) {
      if (item.allChairs) return 'All chairs';
      return item.operatoryNums.map(n => this.opName(n)).filter(Boolean).join(', ');
    },

    formatTime(minutes) {
      return formatMinutes(minutes);
    },

    formatRange(start, end) {
      return formatTimeRange(start, end);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-timeline {
  // Room under the last card so it is not flush against the bottom of the glass.
  padding: 2px 0 32px;
}

.sv-timeline__empty {
  margin: 0 0 12px;
  padding: 18px;
  border: 1px dashed $sv-line;
  border-radius: $sv-radius-lg;
  background: $sv-surface;
  font-size: 14px;
  color: $sv-ink-3;
}

.sv-tl-hour {
  display: flex;
  gap: 6px;
}

.sv-tl-hour__rail {
  position: relative;
  // Narrower than the design's 56px: "12 pm" needs ~34px, and the rest goes to the cards.
  flex: 0 0 40px;
  // The rail's line runs down its right edge; the label sits against it.
  border-right: 1px solid $sv-line;
}

.sv-tl-hour__label {
  display: block;
  padding: 12px 6px 0 0;
  text-align: right;
  font-size: 11.5px;
  font-weight: 700;
  color: $sv-ink-3;
  white-space: nowrap;
}

.sv-tl-hour--past .sv-tl-hour__label { color: #a3aaa5; }

.sv-tl-hour--current {
  .sv-tl-hour__rail { border-right: 2px solid rgba(15, 114, 89, 0.35); }
  .sv-tl-hour__label { color: $sv-brand; }
}

.sv-tl-hour__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0 4px;
}

.sv-tl-now {
  position: relative;
  height: 20px;
  display: flex;
  align-items: center;

  // The line, full width, with a dot centred on the rail's line (which sits in the 6px gap).
  &::before {
    content: '';
    position: absolute;
    left: -7px;
    right: 0;
    top: 50%;
    border-top: 2px solid $sv-brand;
  }

  &::after {
    content: '';
    position: absolute;
    left: -11px;
    top: calc(50% - 3px);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $sv-brand;
  }
}

.sv-tl-now__pill {
  position: relative;
  z-index: 1;
  padding: 2px 8px;
  border-radius: 99px;
  background: $sv-brand;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.sv-tl-blockout {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 7px 10px;
  border-radius: $sv-radius;
  background: repeating-linear-gradient(
    135deg,
    rgba(12, 15, 14, 0.04) 0 7px,
    rgba(12, 15, 14, 0.08) 7px 14px
  );
}

.sv-tl-blockout__name {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $sv-ink-2;
}

.sv-tl-blockout__meta {
  font-size: 11.5px;
  color: $sv-ink-3;
}

.sv-timeline__unplaced {
  margin: 12px 0 0;
  padding: 8px 12px;
  font-size: 12px;
  color: $sv-ink-3;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
}
</style>
